// Thin WebGL2 helpers.
export type GL = WebGL2RenderingContext;

export class Prog {
  p: WebGLProgram;
  private u = new Map<string, WebGLUniformLocation | null>();
  constructor(public gl: GL, vs: string, fs: string) {
    const sh = (t: number, s: string) => {
      const o = gl.createShader(t)!;
      gl.shaderSource(o, s);
      gl.compileShader(o);
      if (!gl.getShaderParameter(o, gl.COMPILE_STATUS) && !gl.isContextLost()) throw new Error(gl.getShaderInfoLog(o) || 'shader');
      return o;
    };
    this.p = gl.createProgram()!;
    gl.attachShader(this.p, sh(gl.VERTEX_SHADER, vs));
    gl.attachShader(this.p, sh(gl.FRAGMENT_SHADER, fs));
    gl.linkProgram(this.p);
    if (!gl.getProgramParameter(this.p, gl.LINK_STATUS) && !gl.isContextLost()) throw new Error(gl.getProgramInfoLog(this.p) || 'link');
  }
  use() { this.gl.useProgram(this.p); return this; }
  loc(n: string) {
    let l = this.u.get(n);
    if (l === undefined) { l = this.gl.getUniformLocation(this.p, n); this.u.set(n, l); }
    return l;
  }
  f(n: string, ...v: number[]) {
    const l = this.loc(n), g = this.gl;
    if (v.length === 1) g.uniform1f(l, v[0]); else if (v.length === 2) g.uniform2f(l, v[0], v[1]);
    else if (v.length === 3) g.uniform3f(l, v[0], v[1], v[2]); else g.uniform4f(l, v[0], v[1], v[2], v[3]);
  }
  i(n: string, v: number) { this.gl.uniform1i(this.loc(n), v); }
  ui(n: string, v: number) { this.gl.uniform1ui(this.loc(n), v); }
  m(n: string, v: Float32Array) { this.gl.uniformMatrix4fv(this.loc(n), false, v); }
  f4v(n: string, v: Float32Array) { if (v.length) this.gl.uniform4fv(this.loc(n), v); }
  iv(n: string, v: Int32Array) { if (v.length) this.gl.uniform1iv(this.loc(n), v); }
}

export function tex(gl: GL, w: number, h: number, internal: number, format: number, type: number, filter: number) {
  const t = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, t);
  gl.texImage2D(gl.TEXTURE_2D, 0, internal, w, h, 0, format, type, null);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, filter);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return t;
}

/** Colour + depth-texture target for the point pass. */
export class Target {
  fb: WebGLFramebuffer; col: WebGLTexture; depth: WebGLTexture;
  constructor(gl: GL, public w: number, public h: number) {
    this.col = tex(gl, w, h, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, gl.LINEAR);
    this.depth = tex(gl, w, h, gl.DEPTH_COMPONENT24, gl.DEPTH_COMPONENT, gl.UNSIGNED_INT, gl.NEAREST);
    this.fb = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.col, 0);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.TEXTURE_2D, this.depth, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }
  free(gl: GL) { gl.deleteFramebuffer(this.fb); gl.deleteTexture(this.col); gl.deleteTexture(this.depth); }
}

/** Two RGBA8 attachments (packed depth, id) + depth renderbuffer for picking. */
export class PickTarget {
  fb: WebGLFramebuffer; a: WebGLTexture; b: WebGLTexture; rb: WebGLRenderbuffer;
  constructor(gl: GL, public w: number, public h: number) {
    this.a = tex(gl, w, h, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, gl.NEAREST);
    this.b = tex(gl, w, h, gl.RGBA8, gl.RGBA, gl.UNSIGNED_BYTE, gl.NEAREST);
    this.rb = gl.createRenderbuffer()!;
    gl.bindRenderbuffer(gl.RENDERBUFFER, this.rb);
    gl.renderbufferStorage(gl.RENDERBUFFER, gl.DEPTH_COMPONENT24, w, h);
    this.fb = gl.createFramebuffer()!;
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.a, 0);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT1, gl.TEXTURE_2D, this.b, 0);
    gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, this.rb);
    gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }
  free(gl: GL) { gl.deleteFramebuffer(this.fb); gl.deleteTexture(this.a); gl.deleteTexture(this.b); gl.deleteRenderbuffer(this.rb); }
}
